<template>
  <div id="mol-member-bulk-upload">
    <v-card class="upload-card" elevation="0" rounded="lg">
      <!-- Header Section -->
      <v-card class="header-card mb-8 overflow-hidden">
        <v-row align="center" class="fill-height pa-6">
          <v-col sm="7" xs="12" class="header-content d-flex align-center">
            <div class="header-icon-box">
              <v-icon size="48" color="white">mdi-cloud-upload</v-icon>
            </div>
            <div class="header-text-content">
              <h1 class="header-title">{{ pageTitle }}</h1>
              <p class="header-subtitle">Upload multiple members at once</p>
            </div>
          </v-col>
          <v-col sm="5" xs="12" class="d-flex justify-end align-center gap-2">
            <v-btn @click="closePrompt" icon rounded class="action-btn" x-large>
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <!-- Content Section -->
      <v-container fluid class="px-6 px-md-8">
        <!-- Sample File Card (shown first) -->
        <v-row class="mb-8">
          <v-col cols="12">
            <v-card class="sample-card" elevation="0">
              <div class="sample-card-content">
                <div class="sample-left-section">
                  <div class="sample-icon-wrapper">
                    <v-icon size="56" color="white" class="sample-icon">
                      mdi-file-document-outline
                    </v-icon>
                  </div>
                </div>
                <div class="sample-middle-section">
                  <h4 class="sample-title">📋 Sample Format Guide</h4>
                  <p class="sample-description">
                    Download our pre-formatted sample file to see exactly how your data should be structured
                  </p>
                </div>
                <div class="sample-right-section">
                  <a
                    :href="SampleFilePath"
                    target="_blank"
                    class="sample-download-btn"
                  >
                    <v-icon class="btn-icon" color="white">mdi-download</v-icon>
                    <span class="btn-text">Download Sample</span>
                  </a>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Instructions Card -->
        <v-card class="instructions-card mb-8" elevation="0">
          <div class="instruction-heading">
            <v-icon class="mr-3">mdi-information-outline</v-icon>
            Upload Instructions
          </div>
          <div class="instruction-content">
            <div class="requirement-box mb-4">
              <v-icon class="mr-3">mdi-check-circle</v-icon>
              <span>Download the sample file to see the required format</span>
            </div>
            <div class="requirement-box mb-4">
              <v-icon class="mr-3">mdi-check-circle</v-icon>
              <span
                >File must be in
                <strong>Excel format (.xlsx, .xls)</strong></span
              >
            </div>
            <div class="requirement-box mb-4">
              <v-icon class="mr-3">mdi-check-circle</v-icon>
              <span
                >Required columns:
                <strong>Member Name, WhatsApp Number, Email ID</strong></span
              >
            </div>
            <div class="requirement-box">
              <v-icon class="mr-3">mdi-check-circle</v-icon>
              <span
                >WhatsApp Number must be <strong>10 digits</strong> and Email
                must be valid</span
              >
            </div>
          </div>
        </v-card>

        <!-- Upload Card -->
        <v-card class="form-card mb-8" elevation="0">
          <v-form ref="uploadForm" v-model="validForm" lazy-validation>
            <!-- File Upload Section -->
            <div class="form-section">
              <h3 class="section-title mb-6">
                <v-icon class="mr-2">mdi-file-excel</v-icon>
                Select File to Upload
              </h3>

              <v-row>
                <v-col cols="12">
                  <div class="form-field">
                    <label class="field-label required-field">Excel File</label>
                    <v-file-input
                      v-model="UploadedFile1"
                      :rules="FileRules"
                      accept=".xls,.xlsx"
                      placeholder="Upload excel file"
                      prepend-icon="mdi-file-excel"
                      truncate-length="25"
                      :clearable="false"
                      show-size
                      dense
                      outlined
                      class="field-input"
                    >
                      <template v-slot:append>
                        <v-icon color="red" @click="UploadedFile1 = []">
                          fa-trash
                        </v-icon>
                      </template>
                    </v-file-input>
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-form>
        </v-card>

        <!-- Upload Progress Card -->
        <v-card class="progress-card mb-8" elevation="0" v-if="submitLoading">
          <v-progress-linear
            :value="uploadProgress"
            color="#0097d7"
            height="25"
            striped
          >
            <template v-slot:default="{ value }">
              <span class="progress-text">{{ Math.ceil(value) }}%</span>
            </template>
          </v-progress-linear>
        </v-card>

        <!-- Results Card -->
        <v-card class="results-card mb-8" elevation="0" v-if="uploadResults">
          <div class="results-header">
            <v-icon color="#4caf50" size="28">mdi-check-circle</v-icon>
            <span>Upload Results</span>
          </div>
          <div class="results-content">
            <!-- Successful Results Card -->
            <div class="result-item success-card">
              <div class="result-card-icon success-icon">
                <v-icon size="32" color="white"
                  >mdi-check-circle-outline</v-icon
                >
              </div>
              <div class="result-card-body">
                <span class="result-label">Successful Uploads</span>
                <div class="result-value-container">
                  <span class="result-value success-value">{{
                    uploadResults && uploadResults.successful_count
                      ? uploadResults.successful_count
                      : 0
                  }}</span>
                  <span class="result-unit">record(s)</span>
                </div>
              </div>
              <div class="result-card-badge success-badge">✓ Complete</div>
            </div>

            <!-- Failed Results Card -->
            <div
              class="result-item error-card"
              v-if="uploadResults && uploadResults.failed_count > 0"
            >
              <div class="result-card-icon error-icon">
                <v-icon size="32" color="white"
                  >mdi-alert-circle-outline</v-icon
                >
              </div>
              <div class="result-card-body">
                <span class="result-label">Failed/Invalid Records</span>
                <div class="result-value-container">
                  <span class="result-value error-value">{{
                    uploadResults.failed_count
                  }}</span>
                  <span class="result-unit">record(s)</span>
                </div>
              </div>
              <div class="result-card-badge error-badge">! Review</div>
            </div>
          </div>

          <!-- Failed Records Table -->
          <div
            class="failed-records mt-6"
            v-if="uploadResults && rows && rows.length > 0"
          >
            <div class="failed-header">
              <v-icon color="#f44336" size="20">mdi-alert-circle</v-icon>
              <span>Upload Issues - {{ rows.length }} Record(s)</span>
            </div>
            <v-data-table
              style="font-size: 40px"
              v-if="rows.length > 0"
              v-model="selected"
              class="custom-data-table"
              item-class="custom-row-style"
              :headers="TableColumns1"
              :items="rows"
              :items-per-page="50"
              :search="search"
              :single-select="false"
              :show-select="false"
              :footer-props="{
                'items-per-page-options': [10, 20, 30, 40, 50],
              }"
            ></v-data-table>
          </div>
        </v-card>

        <!-- Action Buttons -->
        <div class="action-section mb-6">
          <v-row justify="end" class="gap-3">
            <v-col cols="auto">
              <v-btn
                @click.prevent="closePrompt"
                rounded
                outlined
                class="action-cancel-btn"
                min-width="150"
              >
                <v-icon left>mdi-close</v-icon>
                Close
              </v-btn>
            </v-col>
            <v-col cols="auto">
              <v-btn
                :disabled="!UploadedFile1 || submitLoading"
                @click.prevent="submitUpload"
                :loading="submitLoading"
                rounded
                class="action-save-btn"
                min-width="150"
                color="white"
              >
                <v-icon left>mdi-cloud-upload</v-icon>
                Upload
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </v-container>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: {},
  props: {
    BulkUploadPrompt: {
      type: Boolean,
      required: true,
    },
    pageTitle: {
      type: String,
      default: "Bulk Upload Members",
    },
  },
  computed: {
    SampleFilePath() {
      return (
        companyConfig.apiURL + "assets/sample/mol_member_sample_upload.xlsx"
      );
    },
  },
  data() {
    return {
      validForm: true,
      uploadFile: null,
      submitLoading: false,
      uploadProgress: 0,
      uploadInProgress: false,
      uploadResults: null,
      FileRules: [
        (v) => !!v || "File is required",
        (v) =>
          !v ||
          [
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/vnd.ms-excel",
            "text/csv",
          ].includes(v.type) ||
          "File must be Excel format (.xlsx, .xls, .csv)",
      ],
      UploadedFile1: [],
      TableOptions1: {},
      TableColumns1: [],
      rows: [],
      selected: [],
    };
  },
  watch: {},
  created() {},
  methods: {
    closePrompt() {
      console.log("closePrompt called");
      this.$emit("hideBulkUploadPrompt", this.uploadResults ? true : false);
    },

    submitUpload() {
      console.log("submitUpload called");

      var validate1 = this.$refs.uploadForm.validate();
      console.log({ validate1 });

      if (validate1) {
        this.TableOptions1 = {};
        this.TableColumns1 = [];
        this.rows = [];
        this.submitLoading = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url =
          server_url + "api/mission-onelakh/mol-members-bulk-upload";

        let upload = new FormData();
        upload.append("UserInterface", 1);
        upload.append("UploadedFile", this.UploadedFile1);

        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = {};
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log({ response });

            thisIns.submitLoading = false;

            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;

            const tableData =
              records && records.TableData ? records.TableData : [];
            const hasErrorData = tableData && tableData.length > 0;

            console.log({ flag, output, records, tableData, hasErrorData });

            // Always display results if there's data in the table or records
            if (records && records.TableHeader && records.TableData) {
              thisIns.TableOptions1 = records.TableOptions || {};
              thisIns.TableColumns1 = records.TableHeader || [];
              thisIns.rows = tableData;

              thisIns.uploadResults = {
                successful_count: 0, // If showing error table, successes would be in a separate response
                failed_count: tableData.length,
                failed_records: tableData || [],
              };

              // Show appropriate toast based on flag
              if (flag == 1 || flag == true) {
                thisIns.toast("success", output);
              } else {
                thisIns.toast("error", output);
              }
            } else {
              // No table data, just show message
              if (flag == 1 || flag == true) {
                thisIns.toast("success", output);
              } else {
                thisIns.toast("error", output);
              }
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.submitLoading = false;
          });
      } else {
        var message = "Kindly select a file";
        this.toast("error", message);
      }
    },
  },
  beforeMount() {},
};
</script>

<style lang="scss">
// Color Palette - JCI Brand Colors
$navy: #3a67b1;
$aqua: #0097d7;
$gold: #edbe38;
$text-off-black: #0a0f29;
$text-white: #ffffff;
$success-green: #4caf50;
$error-red: #f44336;

// Animations
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-12px);
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

#mol-member-bulk-upload {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 30px 15px;

  .upload-card {
    border-radius: 20px !important;
    background: #ffffff !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .header-card {
    background: linear-gradient(
      135deg,
      $navy 0%,
      $aqua 50%,
      $navy 100%
    ) !important;
    border-radius: 20px !important;
    overflow: hidden;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    border: none;
    box-shadow: 0 20px 60px rgba(58, 103, 177, 0.5),
      0 0 40px rgba(0, 151, 215, 0.3);

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(
        45deg,
        transparent 30%,
        rgba(255, 255, 255, 0.1) 50%,
        transparent 70%
      );
      animation: shimmer 3s infinite;
    }

    .header-content {
      position: relative;
      z-index: 2;
    }
  }

  .header-icon-box {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.25) 0%,
      rgba(255, 255, 255, 0.15) 100%
    );
    backdrop-filter: blur(15px);
    border-radius: 16px;
    padding: 16px;
    width: fit-content;
    flex-shrink: 0;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
    animation: float 4s ease-in-out infinite;
    border: 2px solid rgba(255, 255, 255, 0.3);
    transition: all 0.3s ease;
    margin-right: 20px;

    &:hover {
      transform: scale(1.1);
      box-shadow: 0 16px 48px rgba(0, 0, 0, 0.3),
        inset 0 1px 0 rgba(255, 255, 255, 0.5);
    }
  }

  .header-text-content {
    flex: 1;
    min-width: 0;
  }

  .header-title {
    font-size: 32px;
    font-weight: 900;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 151, 215, 0.3);
    animation: slideInLeft 0.8s ease-out;
    letter-spacing: 0.8px;
    color: $text-white;
    margin: 0;
    padding: 0;
    line-height: 1.2;
  }

  .header-subtitle {
    font-size: 15px;
    opacity: 0.95;
    animation: slideInLeft 1s ease-out 0.1s backwards;
    letter-spacing: 0.3px;
    color: rgba($text-white, 0.9);
    font-weight: 500;
    margin: 6px 0 0 0;
    line-height: 1.4;
  }

  .action-btn {
    background: rgba(255, 255, 255, 0.15) !important;
    border: 2px solid rgba(255, 255, 255, 0.3) !important;
    backdrop-filter: blur(10px);
    color: white !important;
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);

    &:hover {
      transform: translateY(-6px);
      background: rgba(255, 255, 255, 0.25) !important;
      border-color: rgba(255, 255, 255, 0.5) !important;
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3) !important;
    }
  }

  .instructions-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #fff5e6 0%, #fffaf0 100%) !important;
    box-shadow: 0 12px 32px rgba(237, 190, 56, 0.15) !important;
    border: 2px solid rgba(237, 190, 56, 0.25) !important;
    padding: 24px !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s backwards;

    .instruction-heading {
      font-size: 16px;
      font-weight: 800;
      color: $navy;
      display: flex;
      align-items: center;
      margin: 0 0 16px 0;
      text-transform: uppercase;
      letter-spacing: 0.6px;

      .v-icon {
        transition: all 0.3s ease;
      }
      &:hover .v-icon {
        transform: rotate(20deg) scale(1.15);
      }
    }

    .instruction-content {
      .requirement-box {
        display: flex;
        gap: 16px;
        background: linear-gradient(
          135deg,
          rgba(76, 175, 80, 0.12),
          rgba(76, 175, 80, 0.08)
        );
        border-left: 5px solid $success-green;
        padding: 16px 14px;
        border-radius: 12px;
        transition: all 0.3s ease;

        &:hover {
          background: linear-gradient(
            135deg,
            rgba(76, 175, 80, 0.15),
            rgba(76, 175, 80, 0.1)
          );
          box-shadow: 0 6px 16px rgba(76, 175, 80, 0.15);
          transform: translateY(-2px);
        }

        .v-icon {
          flex-shrink: 0;
          transition: all 0.3s ease;
        }
        &:hover .v-icon {
          transform: scale(1.2);
        }

        span {
          color: $text-off-black;
          font-weight: 600;
          font-size: 14px;
          line-height: 1.6;
        }
      }
    }
  }

  .form-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    box-shadow: 0 16px 48px rgba(58, 103, 177, 0.12) !important;
    border: 2px solid rgba(0, 151, 215, 0.1) !important;
    padding: 24px !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.15s backwards;

    .form-section {
      .section-title {
        font-size: 18px;
        font-weight: 800;
        color: $navy;
        display: flex;
        align-items: center;
        margin: 0;
        text-transform: uppercase;
        letter-spacing: 0.6px;
        padding-bottom: 12px;
        border-bottom: 3px solid $aqua;

        .v-icon {
          transition: all 0.3s ease;
        }
        &:hover .v-icon {
          transform: rotate(-10deg) scale(1.15);
        }
      }

      .form-field {
        margin-bottom: 8px;

        .field-label {
          font-size: 13px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: $navy;
          display: block;
          margin-bottom: 8px;

          &.required-field::after {
            content: "*";
            color: $error-red;
            margin-left: 4px;
          }
        }

        .field-input {
          transition: all 0.3s ease;

          &::v-deep {
            .v-input__prepend-outer {
              margin-right: 8px !important;
              .v-icon {
                color: $aqua;
              }
            }

            .v-input__control {
              .v-input__slot {
                input,
                textarea {
                  color: $text-off-black;
                  font-weight: 600;

                  &::placeholder {
                    color: #aaa !important;
                    font-weight: 500;
                  }
                }
              }
            }

            fieldset {
              border-color: rgba(0, 151, 215, 0.2) !important;
            }

            &.v-input--is-focused {
              fieldset {
                border-color: $aqua !important;
              }
              .v-icon {
                color: $navy;
              }
            }
          }
        }
      }
    }
  }

  .progress-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%) !important;
    box-shadow: 0 12px 32px rgba(58, 103, 177, 0.12) !important;
    border: 2px solid rgba(0, 151, 215, 0.1) !important;
    padding: 24px !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s backwards;

    .progress-text {
      font-weight: 700;
      color: $navy;
      font-size: 12px;
    }
  }

  .results-card {
    border-radius: 16px !important;
    background: linear-gradient(135deg, #f0f7ff 0%, #f8fafc 100%) !important;
    box-shadow: 0 12px 32px rgba(76, 175, 80, 0.15) !important;
    border: 2px solid rgba(76, 175, 80, 0.25) !important;
    padding: 24px !important;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.25s backwards;

    .results-header {
      font-size: 16px;
      font-weight: 800;
      color: $navy;
      display: flex;
      align-items: center;
      gap: 12px;
      margin: 0 0 20px 0;
      text-transform: uppercase;
      letter-spacing: 0.6px;
    }

    .results-content {
      display: flex;
      gap: 24px;
      margin-bottom: 20px;
      flex-wrap: wrap;
      justify-content: flex-start;

      @media (max-width: 768px) {
        gap: 16px;
        flex-direction: column;
      }

      .result-item {
        flex: 1;
        min-width: 280px;
        display: flex;
        align-items: center;
        gap: 20px;
        padding: 24px;
        border-radius: 16px;
        position: relative;
        overflow: hidden;
        transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
        border: 2px solid;
        animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s
          backwards;

        &::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(
            135deg,
            transparent 0%,
            rgba(255, 255, 255, 0.1) 50%,
            transparent 100%
          );
          pointer-events: none;
        }

        &:hover {
          transform: translateY(-8px);
          box-shadow: 0 16px 40px rgba(0, 0, 0, 0.15);
        }

        @media (max-width: 768px) {
          min-width: 100%;
          padding: 20px;
          gap: 16px;
        }

        &.success-card {
          background: linear-gradient(
            135deg,
            #e8f5e9 0%,
            #f1f8e9 100%
          ) !important;
          border-color: rgba(76, 175, 80, 0.3) !important;

          &:hover {
            background: linear-gradient(
              135deg,
              #e0f2f1 0%,
              #e8f5e9 100%
            ) !important;
            box-shadow: 0 16px 40px rgba(76, 175, 80, 0.2) !important;
          }
        }

        &.error-card {
          background: linear-gradient(
            135deg,
            #ffebee 0%,
            #fff3e0 100%
          ) !important;
          border-color: rgba(244, 67, 54, 0.3) !important;

          &:hover {
            background: linear-gradient(
              135deg,
              #fff3e0 0%,
              #ffe0b2 100%
            ) !important;
            box-shadow: 0 16px 40px rgba(244, 67, 54, 0.2) !important;
          }
        }

        .result-card-icon {
          flex-shrink: 0;
          width: 80px;
          height: 80px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          z-index: 2;
          animation: float 4s ease-in-out infinite;

          &.success-icon {
            background: linear-gradient(135deg, #4caf50 0%, #66bb6a 100%);
            box-shadow: 0 8px 20px rgba(76, 175, 80, 0.3);
          }

          &.error-icon {
            background: linear-gradient(135deg, #f44336 0%, #ef5350 100%);
            box-shadow: 0 8px 20px rgba(244, 67, 54, 0.3);
          }
        }

        .result-card-body {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 8px;
          position: relative;
          z-index: 2;

          .result-label {
            font-weight: 700;
            color: $navy;
            font-size: 13px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            opacity: 0.8;
          }

          .result-value-container {
            display: flex;
            align-items: baseline;
            gap: 8px;

            .result-value {
              font-size: 36px;
              font-weight: 900;
              transition: all 0.3s ease;

              &.success-value {
                color: #4caf50;
                text-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
              }

              &.error-value {
                color: #f44336;
                text-shadow: 0 2px 8px rgba(244, 67, 54, 0.3);
              }
            }

            .result-unit {
              font-size: 13px;
              font-weight: 600;
              color: $navy;
              opacity: 0.7;
              text-transform: uppercase;
              letter-spacing: 0.3px;
            }
          }
        }

        .result-card-badge {
          flex-shrink: 0;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 11px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          position: relative;
          z-index: 2;
          white-space: nowrap;
          animation: slideInDown 0.8s ease-out 0.4s backwards;

          &.success-badge {
            background: rgba(76, 175, 80, 0.2);
            color: #4caf50;
            border: 1px solid rgba(76, 175, 80, 0.4);
          }

          &.error-badge {
            background: rgba(244, 67, 54, 0.2);
            color: #f44336;
            border: 1px solid rgba(244, 67, 54, 0.4);
          }
        }
      }
    }

    .failed-records {
      border-top: 2px solid rgba(244, 67, 54, 0.15);
      padding-top: 20px;

      .failed-header {
        font-size: 14px;
        font-weight: 800;
        color: $error-red;
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0 0 16px 0;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .failed-table {
        &::v-deep {
          thead tr th {
            background: linear-gradient(
              135deg,
              #fff5f5 0%,
              #ffe0e0 100%
            ) !important;
            font-weight: 800 !important;
            color: $error-red !important;
            border: none !important;
          }

          tbody tr {
            transition: all 0.3s ease;
            border-bottom: 1px solid #f5f5f5 !important;

            &:hover {
              background: rgba(244, 67, 54, 0.05) !important;
            }

            td {
              color: $text-off-black;
              font-size: 13px;
              padding: 12px 16px !important;
            }
          }
        }
      }
    }
  }

  .sample-card {
    background: linear-gradient(
      135deg,
      rgba(0, 151, 215, 0.08) 0%,
      rgba(58, 103, 177, 0.08) 100%
    ) !important;
    border: 2px solid rgba(0, 151, 215, 0.2) !important;
    border-radius: 16px !important;
    position: relative;
    overflow: hidden;
    animation: slideInUp 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s backwards;
    padding: 32px !important;

    &::before {
      content: "";
      position: absolute;
      top: -50%;
      right: -50%;
      width: 300px;
      height: 300px;
      background: radial-gradient(
        circle,
        rgba(0, 151, 215, 0.15) 0%,
        transparent 70%
      );
      animation: float 6s ease-in-out infinite;
      pointer-events: none;
    }

    &::after {
      content: "";
      position: absolute;
      bottom: -30%;
      left: -10%;
      width: 250px;
      height: 250px;
      background: radial-gradient(
        circle,
        rgba(58, 103, 177, 0.1) 0%,
        transparent 70%
      );
      animation: float 5s ease-in-out infinite 0.5s;
      pointer-events: none;
    }
  }

  .sample-card-content {
    display: flex;
    align-items: center;
    gap: 32px;
    position: relative;
    z-index: 2;
    flex-wrap: wrap;

    @media (max-width: 960px) {
      flex-direction: column;
      text-align: center;
      gap: 24px;
    }
  }

  .sample-left-section {
    flex-shrink: 0;
    animation: slideInLeft 0.8s ease-out 0.2s backwards;

    @media (max-width: 960px) {
      animation: slideInUp 0.8s ease-out 0.2s backwards;
    }
  }

  .sample-middle-section {
    flex: 1;
    min-width: 250px;
    animation: slideInLeft 0.8s ease-out 0.3s backwards;

    @media (max-width: 960px) {
      animation: slideInUp 0.8s ease-out 0.3s backwards;
    }
  }

  .sample-right-section {
    flex-shrink: 0;
    animation: slideInUp 0.8s ease-out 0.4s backwards;

    @media (max-width: 960px) {
      width: 100%;
      display: flex;
      justify-content: center;
    }
  }

  .sample-icon-wrapper {
    width: 100px;
    height: 100px;
    border-radius: 16px;
    background: linear-gradient(135deg, $navy 0%, $aqua 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 12px 32px rgba(0, 151, 215, 0.25);
    position: relative;
    z-index: 2;

    .sample-icon {
      animation: float 4s ease-in-out infinite;
    }

    &:hover {
      transform: scale(1.1) rotate(5deg);
      box-shadow: 0 16px 40px rgba(0, 151, 215, 0.35);
    }
  }

  .sample-text-content {
    position: relative;
    z-index: 2;
    animation: slideInLeft 0.8s ease-out 0.3s backwards;

    .sample-title {
      font-size: 18px;
      font-weight: 800;
      color: $navy;
      margin: 0 0 12px 0;
      letter-spacing: 0.5px;
      text-transform: uppercase;
    }

    .sample-description {
      font-size: 14px;
      color: #666;
      margin: 0;
      line-height: 1.6;
      font-weight: 500;
      max-width: 350px;
    }
  }

  .sample-download-btn {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 14px 28px;
    background: linear-gradient(135deg, $navy 0%, $aqua 100%);
    color: white;
    text-decoration: none;
    border-radius: 12px;
    font-weight: 700;
    font-size: 14px;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    box-shadow: 0 12px 32px rgba(0, 151, 215, 0.3),
      0 0 20px rgba(58, 103, 177, 0.15);
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    z-index: 2;
    overflow: hidden;
    animation: slideInUp 0.8s ease-out 0.4s backwards;
    cursor: pointer;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.3),
        transparent
      );
      animation: shimmer 2s infinite;
      transition: all 0.4s ease;
    }

    &:hover {
      transform: translateY(-6px) scale(1.05);
      box-shadow: 0 20px 48px rgba(0, 151, 215, 0.4),
        0 0 30px rgba(58, 103, 177, 0.25);

      .btn-icon {
        animation: bounce 0.6s ease-in-out;
      }

      .btn-text {
        animation: slideInDown 0.6s ease-out;
      }
    }

    &:active {
      transform: translateY(-2px) scale(1.03);
    }

    .btn-icon {
      font-size: 20px;
      transition: all 0.3s ease;
    }

    .btn-text {
      transition: all 0.3s ease;
      display: inline-block;
    }
  }

  @keyframes bounce {
    0% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-8px);
    }
    100% {
      transform: translateY(0);
    }
  }

  @keyframes slideInDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .download-btn {
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 20px rgba(0, 151, 215, 0.2) !important;
    }
  }

  .action-section {
    padding: 20px 0;
    border-top: 2px solid #f0f0f0;
    margin-top: 20px;

    .gap-3 {
      gap: 16px;
    }

    .action-cancel-btn {
      font-weight: 700 !important;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      color: #666 !important;
      border-color: #ddd !important;
      transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);

      &:hover {
        border-color: $aqua !important;
        color: $aqua !important;
        transform: translateY(-4px);
        box-shadow: 0 8px 20px rgba(0, 151, 215, 0.2) !important;
      }

      .v-icon {
        transition: all 0.3s ease;
      }
      &:hover .v-icon {
        transform: scale(1.15);
      }
    }

    .action-save-btn {
      background: linear-gradient(135deg, $navy 0%, $aqua 100%) !important;
      font-weight: 700 !important;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      color: white !important;
      box-shadow: 0 12px 32px rgba(0, 151, 215, 0.3) !important;
      transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);

      &:hover:not(:disabled) {
        transform: translateY(-6px);
        box-shadow: 0 16px 48px rgba(0, 151, 215, 0.4) !important;
      }

      &:disabled {
        background: linear-gradient(135deg, #ccc 0%, #aaa 100%) !important;
        box-shadow: none !important;
      }

      .v-icon {
        color: white !important;
        transition: all 0.3s ease;
      }
      &:hover:not(:disabled) .v-icon {
        transform: scale(1.2);
      }
    }
  }

  @media (max-width: 960px) {
    padding: 20px 12px;

    .header-card {
      border-radius: 16px;
    }
    .header-title {
      font-size: 26px;
    }
    .header-subtitle {
      font-size: 14px;
    }
    .section-title {
      font-size: 16px;
    }
    .form-card,
    .instructions-card {
      border-radius: 14px;
    }

    .action-section {
      .v-row > * {
        margin: 8px 0;
      }
    }
  }

  @media (max-width: 600px) {
    padding: 16px 10px;

    .header-card {
      border-radius: 14px;
    }
    .header-title {
      font-size: 20px;
    }
    .header-subtitle {
      font-size: 12px;
    }
    .section-title {
      font-size: 14px;
    }
    .form-card,
    .instructions-card {
      border-radius: 12px;
      padding: 16px !important;
    }

    .action-section {
      flex-direction: column;

      .action-cancel-btn,
      .action-save-btn {
        width: 100% !important;
        min-width: unset !important;
      }

      .v-row > * {
        margin: 6px 0;
      }
    }
  }
}
</style>
